// Amazon Associates affiliate configuration.
//
// Everything monetization-related lives here so it can be maintained in one
// place. To change what a banner promotes, edit the catalog below. To change
// the account, update AMAZON_ASSOCIATE_TAG.
//
// Compliance notes:
// - All links point to amazon.com.br and carry our associate tag, so every
//   locale earns (the tag only works on the Brazilian marketplace).
// - Links use search queries (keyword + tag) instead of hardcoded product
//   ASINs, so they never go dead. Swap `keyword` for a direct product URL any
//   time you want to feature a specific item.
// - A required disclosure ("As an Amazon Associate I earn from qualifying
//   purchases") is rendered on every card by the AdSpace component.

export const AMAZON_ASSOCIATE_TAG = 'rodadavida05-20';
export const AMAZON_DOMAIN = 'https://www.amazon.com.br';

export type AffiliateLocale = 'pt-BR' | 'en';

export interface AffiliateProduct {
  /** Amazon search keyword used to build the affiliate URL. */
  keyword: string;
  title: Record<AffiliateLocale, string>;
  description: Record<AffiliateLocale, string>;
  cta: Record<AffiliateLocale, string>;
}

export type AdPosition =
  | 'header'
  | 'sidebar'
  | 'inline'
  | 'footer'
  | 'post-completion';

/**
 * Normalize an i18next language code (e.g. "pt", "pt-BR", "en-US") to one of
 * our supported affiliate locales, defaulting to Portuguese.
 */
export function resolveLocale(language: string | undefined): AffiliateLocale {
  return language && language.toLowerCase().startsWith('en') ? 'en' : 'pt-BR';
}

/** Build an amazon.com.br search URL carrying our associate tag. */
export function buildAmazonUrl(keyword: string): string {
  const params = new URLSearchParams({ k: keyword, tag: AMAZON_ASSOCIATE_TAG });
  return `${AMAZON_DOMAIN}/s?${params.toString()}`;
}

/**
 * Append our associate tag to any direct amazon.com.br product URL. Paste the
 * product link straight from the browser (or SiteStripe) and this guarantees
 * the tag is present. Falls back to returning the input unchanged if it isn't
 * a valid URL.
 */
export function withAmazonTag(productUrl: string): string {
  try {
    const url = new URL(productUrl);
    url.searchParams.set('tag', AMAZON_ASSOCIATE_TAG);
    return url.toString();
  } catch {
    return productUrl;
  }
}

export interface AffiliateBook {
  id: string;
  title: string;
  author?: string;
  /**
   * Cover image. Recommended: save the cover to `public/books/` and reference
   * it here as `/books/<file>.jpg`. A full https URL also works if you host it
   * elsewhere. Grab covers via Amazon SiteStripe while logged in as an
   * Associate — do not hotlink images directly from Amazon's product pages.
   */
  image: string;
  /** Direct amazon.com.br product URL. The associate tag is added for you. */
  url: string;
}

/** Localized heading for the book showcase section. */
export const SHOWCASE_HEADING: Record<AffiliateLocale, string> = {
  'pt-BR': 'Livros que recomendo',
  en: 'Books I recommend',
};

export const SHOWCASE_SUBHEADING: Record<AffiliateLocale, string> = {
  'pt-BR': 'Leituras para trabalhar cada área da sua roda da vida.',
  en: 'Reads to work on each area of your wheel of life.',
};

export const SHOWCASE_CTA: Record<AffiliateLocale, string> = {
  'pt-BR': 'Ver na Amazon',
  en: 'View on Amazon',
};

/**
 * YOUR curated book list. Add/remove entries freely — this is the only place
 * you need to edit. Each `url` is a direct product link (tag added
 * automatically); each `image` should live in `public/books/`.
 *
 * The entries below are examples to show the format — replace the `url` and
 * `image` values with your own once you have them from SiteStripe.
 */
export const AFFILIATE_BOOKS: AffiliateBook[] = [
  {
    id: 'a-arte-de-gastar-dinheiro',
    title: 'A arte de gastar dinheiro: Escolhas simples para uma vida equilibrada',
    author: 'Morgan Housel',
    image: '/books/a-arte-de-gastar-dinheiro.jpg',
    url: 'https://link.amazon/B0cZXrcy9',
  },
  {
    id: 'psicologia-financeira',
    title: 'A psicologia financeira: lições atemporais sobre fortuna, ganância e felicidade',
    author: 'Morgan Housel',
    image: '/books/psicologia-financeira.jpg',
    url: 'https://link.amazon/B03oYdnzf',
  },
  {
    id: 'mude-seus-horarios-mude-sua-vida',
    title: 'Mude seus horários, mude sua vida: Como usar o relógio biológico para perder peso, reduzir o estresse, dormir melhor e ter mais saúde e energia',
    author: 'Suhas Kshirsagar',
    image: '/books/mude-seus-horarios-mude-sua-vida.jpg',
    url: 'https://link.amazon/B0ibkVfFt',
  },
  {
    id: 'rapido-e-devagar-duas-formas-de-pensar',
    title: 'Rápido e devagar: Duas formas de pensar',
    author: 'Daniel Kahneman',
    image: '/books/rapido-e-devagar-duas-formas-de-pensar.jpg',
    url: 'https://link.amazon/B03KTpiMu',
  },
];

/**
 * Warm, personal message explaining how buying through the links supports the
 * project. This is the friendly line shown to visitors. Note: it does NOT
 * replace the official Amazon disclosure below — Amazon requires that exact
 * wording to remain visible, so both are shown together.
 */
export const AFFILIATE_SUPPORT_MESSAGE: Record<AffiliateLocale, string> = {
  'pt-BR':
    'Quando você compra um livro pela Amazon usando os meus links, uma partezinha volta pra mim, sem nenhum custo a mais pra você, e ajuda a manter a Roda da Vida no ar e gratuita. Obrigada por apoiar 💛',
  en: 'When you buy a book on Amazon through my links, a little bit comes back to me, at no extra cost to you, and helps keep the Wheel of Life free and online. Thank you for the support 💛',
};

/** Official Amazon Associates disclosure (required, kept small but visible). */
export const AFFILIATE_DISCLOSURE: Record<AffiliateLocale, string> = {
  'pt-BR': 'Como Associado da Amazon, ganho com compras qualificadas.',
  en: 'As an Amazon Associate I earn from qualifying purchases.',
};

/** Short partner label shown as a badge in the corner of each card. */
export const AFFILIATE_BADGE: Record<AffiliateLocale, string> = {
  'pt-BR': 'Recomendação',
  en: 'Recommended',
};

/**
 * Curated product recommendation per banner position. Each entry maps to a
 * personal-development theme that fits the Wheel of Life audience.
 */
export const AFFILIATE_PRODUCTS: Record<AdPosition, AffiliateProduct> = {
  header: {
    keyword: 'livros desenvolvimento pessoal',
    title: {
      'pt-BR': 'Comece sua transformação',
      en: 'Start your transformation',
    },
    description: {
      'pt-BR':
        'Os livros de desenvolvimento pessoal mais recomendados para evoluir em cada área da vida.',
      en: 'Top-rated personal development books to grow in every area of your life.',
    },
    cta: {
      'pt-BR': 'Ver livros na Amazon',
      en: 'Browse books on Amazon',
    },
  },

  sidebar: {
    keyword: 'planner metas e organizacao',
    title: {
      'pt-BR': 'Organize suas metas',
      en: 'Organize your goals',
    },
    description: {
      'pt-BR':
        'Planners e diários para transformar sua roda da vida em um plano de ação.',
      en: 'Planners and journals to turn your wheel of life into an action plan.',
    },
    cta: {
      'pt-BR': 'Ver planners',
      en: 'Shop planners',
    },
  },

  inline: {
    keyword: 'essencialismo greg mckeown',
    title: {
      'pt-BR': 'Foco no que importa',
      en: 'Focus on what matters',
    },
    description: {
      'pt-BR':
        'Leituras selecionadas para as áreas onde você quer mais equilíbrio.',
      en: 'Curated reads for the areas where you want more balance.',
    },
    cta: {
      'pt-BR': 'Ver recomendação',
      en: 'See recommendation',
    },
  },

  'post-completion': {
    keyword: 'habitos atomicos james clear',
    title: {
      'pt-BR': 'Coloque seus resultados em prática',
      en: 'Put your results into action',
    },
    description: {
      'pt-BR':
        'Livros sobre hábitos e metas para trabalhar suas áreas de menor pontuação.',
      en: 'Books on habits and goals to work on your lowest-rated areas.',
    },
    cta: {
      'pt-BR': 'Ver na Amazon',
      en: 'View on Amazon',
    },
  },

  footer: {
    keyword: 'livros autoajuda mais vendidos',
    title: {
      'pt-BR': 'Continue crescendo',
      en: 'Keep growing',
    },
    description: {
      'pt-BR': 'Os títulos de autoajuda mais vendidos para cada área da vida.',
      en: 'Best-selling self-help titles for every area of life.',
    },
    cta: {
      'pt-BR': 'Explorar',
      en: 'Explore',
    },
  },
};
